import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import { getNetworkError } from 'common/utils';
import { Spinner } from 'ui';

import appSettings from '../app-settings';
import RightContent from '../components/signup/RightContent';
import AlertMessage from '../components/alerts/AlertMessage';
import BuildPlanForm from '../components/signup/BuildPlanForm';
import SignupForm from '../components/signup/SignupForm';
import { getUserRole, sigInWithEmailAndPassword } from '../core/utils';
import { useAccount } from '../store/account/hooks';

const INVITES_API_URL = `${appSettings.baseUrl}/customer-management/invites`;
const ONBOARDING_USER_API_URL = `${appSettings.baseUrl}/onboarding`;

const SignUp = () => {
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validInvite, setValidInvite] = useState(false);
  const [subscriptionInvite, setSubscriptionInvite] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { setUserRole } = useAccount();

  const authenticateInvite = useCallback(async () => {
    setActiveStep(1);
    setLoadingInvite(true);
    try {
      const response = await axios.get(`${INVITES_API_URL}/${inviteId}`);
      const isValid = response.data.status === 'pending';
      setValidInvite(isValid);
      if (!isValid) throw new Error('Invalid invite');
      setSubscriptionInvite(response.data.hasSubscription);
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setLoadingInvite(false);
  }, [inviteId]);

  useEffect(() => {
    if (!inviteId) {
      setLoadingInvite(false);
      return;
    }

    authenticateInvite();
  }, [inviteId, authenticateInvite]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setError(false);
  };

  const handleSignupSubmit = async (values) => {
    setLoading(true);
    try {
      await axios.post(`${ONBOARDING_USER_API_URL}/user`, {
        name: values.organizationName,
        taxId: values.taxId,
        address: values.legalAddress,
        email: values.email,
        password: values.password,
        inviteId,
        subscription,
      });

      setSuccess('Account created successfully!');

      await sigInWithEmailAndPassword(values.email, values.password);

      const role = getUserRole();
      setUserRole(role);
      navigate('/');
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }
    setLoading(false);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return (
          <SignupForm
            disabled={loading || (!!inviteId && !validInvite)}
            loading={loading}
            onSubmit={handleSignupSubmit}
            displayLoginPrompt={!subscriptionInvite}
            displayOrgFields={subscriptionInvite || !inviteId}
            termsFieldLabel={
              subscriptionInvite || !inviteId ? (
                <>
                  By creating an account, you agree to our{' '}
                  <a
                    href="https://www.donorspring.com/legal"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-700"
                  >
                    MSA
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.donorspring.com/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-700"
                  >
                    Privacy Policy
                  </a>
                </>
              ) : undefined
            }
          />
        );
      case 0:
      default:
        return (
          <BuildPlanForm
            disabled={loading}
            onSubmit={({ subscription }) => {
              setSubscription(subscription);
              setActiveStep(1);
            }}
          />
        );
    }
  };

  return (
    <>
      <div className="flex grow overflow-y-hidden bg-white-100">
        <div className="relative flex-1">
          {loadingInvite ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            renderStep()
          )}
        </div>

        <div className="flex-1">
          <RightContent />
        </div>
      </div>

      <AlertMessage
        open={!!success}
        message={typeof success === 'string' ? success : 'Operation completed successfully!'}
        onClose={() => setSuccess(false)}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        severity="error"
        onClose={handleClose}
        autoHideDuration={null}
      />
    </>
  );
};

export default SignUp;
